import React, { useMemo, useState, useEffect } from 'react';
import moment from 'moment';
import { isEqual } from 'lodash-es';
import { history, useDispatch } from 'umi';
import { Button, Card, Layout } from 'tea-component';

import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { APPMAGIC_TAG_TITLE_MAP } from '@intelligence/components/Filter/FilterBase/_config';
import { Relation } from '@intelligence/components/GenreSelect/enums';
import useGenreSourcesOptions from '@intelligence/components/GenreSelectMiddleware/_hooks/useGenreSourcesOptions';
import '@intelligence/v3/filterControls';
import Filter from '@intelligence/v3/components/Filter/index';
import { buildFilterApiParams } from '@intelligence/v3/filterControls/apiParams';
import { FILTER_TYPE } from '@intelligence/v3/filterControls/filterTypes';
import useFilterablePage from '@intelligence/v3/_hooks/useFilterablePage';
import { FilterMergeStrategy } from '@intelligence/v3/_hooks/useFilterablePage/enums';
import { formatSearchParamsForDisplay } from '@intelligence/v3/utils/urlSearchParams';

import { navigateWithDraftInheritance } from './navigation';
import { buildFilterablePageDemoPanelItems, mockFetchOptions } from './shared';

import type { IFilterGroup, IFilterItemConfig } from '@intelligence/v3/filterCore/types';
import type { IFilterSyncResult } from '@intelligence/v3/_hooks/useFilterablePage/types';
import type { IFilterablePageDemoPanelItem, IFilterablePageDemoValues } from './shared';

import './index.scss';

const { Content } = Layout;

const PAGE_ID = 'filterablePageDemo';
const FILTER_SAVE_TYPE = 'filterable-page-demo';
const INHERIT_SOURCE_PAGE_ID = PAGE_ID;
const EMPTY_SYNC_RESULT: IFilterSyncResult = {
  hasError: false,
  results: [],
};

const DEMO_FILTER_GROUPS: IFilterGroup[] = [
  {
    key: 'basic',
    title: 'dbi_filter_group_popular',
    filterKeys: [
      'rangeTime',
      'myDate',
      'region',
      'myRegion',
      'genre',
      'allGames',
      'dataSource',
      'platForm',
      'allCompanys',
      'releaseTime',
    ],
  },
  {
    key: 'performance',
    title: 'dbi_filter_group_game_performance',
    filterKeys: ['activeUsersRange', 'revenueRange', 'arpuRange', 'downloadsRange', 'revenueGrowth'],
  },
  {
    key: 'sensor_tower',
    title: 'dbi_filter_group_sensor_tower',
    icon: getDataSourceIcon('sensortower'),
    filterKeys: [
      'sensor_tower_tag_art_style',
      'sensor_tower_tag_camera_pov',
      'sensor_tower_tag_product_model',
      'sensor_tower_tag_setting',
      'sensor_tower_tag_tags',
      'sensor_tower_tag_theme',
      'sensor_tower_tag_monetization',
    ],
  },
  {
    key: 'app_magic',
    title: 'dbi_filter_group_app_magic',
    icon: getDataSourceIcon('appmagic'),
    filterKeys: ['appmagic_themes', 'appmagic_features', 'appmagic_ip', 'appmagic_settings', 'appmagic_art_styles'],
  },
  {
    key: 'igdb',
    title: 'dbi_filter_group_igdb',
    icon: getDataSourceIcon('igdb'),
    filterKeys: ['game_engine'],
  },
];

const MOCK_COMPANY_OPTIONS = ['Tencent', 'NetEase', 'miHoYo', 'Supercell', 'Lilith Games', 'FunPlus', 'Scopely'];

const DEFAULT_VALUES: IFilterablePageDemoValues = {
  rangeTime: {
    granularity: 'daily',
    startDate: '2026-01-01',
    endDate: '2026-01-18',
  },
  myDate: {
    granularity: 'monthly',
    startDate: '2026-01-01',
    endDate: '2026-03-31',
  },
  region: ['us', 'fr'],
  myRegion: ['us', 'ca'],
  genre: {
    source: 'sensortower',
    genres: [
      {
        value: 'Action',
        relation: Relation.Or,
        children: [{ value: 'Action Sandbox' }, { value: 'Artillery Shooter' }, { value: 'Brawl' }],
      },
      {
        value: 'Casino',
        relation: Relation.Or,
        children: [{ value: 'Coin Looters' }, { value: 'Dominoes' }],
      },
    ],
  },
  allGames: {
    value: [],
    keywords: 'clash',
    isSearchAll: false,
    checkedList: [],
  },
  dataSource: ['sensortower'],
  platForm: ['appstore', 'googleplay'],
  activeUsersRange: { min: 50000, max: 5000000 },
  revenueRange: { min: 20000, max: 1000000 },
  arpuRange: { min: 1, max: 50 },
  downloadsRange: { min: 10000, max: 1000000 },
  revenueGrowth: { opt: '<', value: '20' },
  allCompanys: ['Tencent', 'NetEase'],
  releaseTime: {
    granularity: 'daily',
    startDate: '2020-01-10',
    endDate: '2026-04-10',
  },
  sensor_tower_tag_art_style: ['Anime'],
  sensor_tower_tag_camera_pov: ['Third Person'],
  sensor_tower_tag_product_model: ['Multiplayer'],
  sensor_tower_tag_setting: ['Fantasy'],
  sensor_tower_tag_tags: ['Gacha'],
  sensor_tower_tag_theme: ['RPG@Fantasy'],
  sensor_tower_tag_monetization: ['In-App Purchase'],
  game_engine: {
    value: ['unity', 'unreal'],
    logic: 'OR',
  },
  appmagic_themes: ['Cozy'],
  appmagic_features: ['Merge'],
  appmagic_ip: ['Anime'],
  appmagic_settings: ['Medieval'],
  appmagic_art_styles: ['Low Poly'],
};

/**
 * 生成 Demo 筛选器配置。
 * 这里集中演示 URL 字段映射、API 字段映射和继承配置。
 */
const getFilterItems = (params: { genreOptions: any; mockOptions: Record<string, any> }): IFilterItemConfig[] => {
  const { genreOptions, mockOptions } = params;

  return [
    {
      type: FILTER_TYPE.RANGE_PICKER,
      field: 'rangeTime',
      apiParamsFieldMapResolver: () => ({
        granularity: 'granularity',
        startDate: 'start_date',
        endDate: 'end_date',
      }),
      urlParamsFieldMapResolver: () => ({
        granularity: 'granularity',
        startDate: 'startDate',
        endDate: 'endDate',
      }),
      controlProps: {
        showSelectType: true,
        typeList: ['date', 'week', 'month'],
      },
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.RANGE_PICKER,
      field: 'myDate',
      apiParamsFieldMapResolver: () => ({
        granularity: 'my_granularity',
        startDate: 'my_start_date',
        endDate: 'my_end_date',
      }),
      urlParamsFieldMapResolver: () => ({
        granularity: 'myGranularity',
        startDate: 'myStartDate',
        endDate: 'myEndDate',
      }),
      controlProps: {
        showSelectType: true,
        typeList: ['date', 'week', 'month'],
      },
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.REGION_SELECT,
      field: 'region',
      apiParamsFieldMapResolver: () => ({
        markets: 'markets',
      }),
      urlParamsFieldMapResolver: () => ({
        markets: 'markets',
      }),
      controlProps: {
        options: mockOptions.regionOptions || [],
        entityType: 'mobile',
      },
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.REGION_SELECT,
      field: 'myRegion',
      apiParamsFieldMapResolver: () => ({
        markets: 'my_markets',
      }),
      urlParamsFieldMapResolver: () => ({
        markets: 'myMarkets',
      }),
      controlProps: {
        options: mockOptions.regionOptions || [],
        entityType: 'mobile',
      },
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.GENRE_SELECT,
      field: 'genre',
      controlProps: {
        sources: ['annie', 'sensortower'],
        genresOptionsData: genreOptions,
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.GAME_SELECT,
      field: 'allGames',
      controlProps: {
        entityKey: 'app_id',
        isStudio: '',
        hasShortCut: false,
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.DATA_SOURCE,
      field: 'dataSource',
      controlProps: {},
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.PLATFORM,
      field: 'platForm',
      controlProps: {
        options: mockOptions.platformOptions || [],
      },
      inherit: ['all'],
    },
    {
      type: FILTER_TYPE.NUMBER_RANGE,
      field: 'activeUsersRange',
      label: 'dbi_active_users_range',
      controlProps: {},
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.REVENUE_RANGE,
      field: 'revenueRange',
      label: 'dbi_revenue_range',
      controlProps: { unitText: '$' },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.REVENUE_RANGE,
      field: 'arpuRange',
      label: 'dbi_arpu_range',
      controlProps: {
        unitText: '$',
        clickSetDefault: true,
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.NUMBER_RANGE,
      field: 'downloadsRange',
      label: 'dbi_downloads_range',
      controlProps: {},
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.REVENUE_GROWTH,
      field: 'revenueGrowth',
      label: 'dbi_revenue_growth',
      controlProps: {
        placeholderText: 'dbi_input_growth',
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.COMPANY_SELECT,
      field: 'allCompanys',
      controlProps: {
        entityType: 'mobile',
        source: 'sensortower',
        dataSources: ['ios', 'google_play'],
        metric: 'downloads',
        regionType: 'region',
        regions: ['global'],
        granularity: 'date',
        start_date: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        end_date: moment().subtract(1, 'days').format('YYYY-MM-DD'),
        getCompanies: async (paramsValue: { search_name?: string }) => {
          const keywords = paramsValue?.search_name?.trim().toLowerCase() || '';
          const companyName = MOCK_COMPANY_OPTIONS.filter((item) => {
            return item.toLowerCase().includes(keywords);
          }).map((item) => {
            return { companyName: item };
          });

          return Promise.resolve({
            data: {
              filter_flag: false,
              company_name: companyName,
            },
          });
        },
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.RANGE_PICKER,
      field: 'releaseTime',
      apiParamsFieldMapResolver: () => ({
        granularity: 'release_granularity',
        startDate: 'release_start_date',
        endDate: 'release_end_date',
      }),
      urlParamsFieldMapResolver: () => ({
        granularity: 'releaseGranularity',
        startDate: 'releaseStartDate',
        endDate: 'releaseEndDate',
      }),
      controlProps: {
        showSelectType: false,
        typeList: ['date'],
        showShortcut: false,
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.TAG_SELECT,
      field: 'sensor_tower_tag_art_style',
      label: 'dbi_detail_art_style',
      controlProps: {
        options: mockOptions.sensorTowerTagOptions?.sensor_tower_tag_art_style || [],
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.TAG_SELECT,
      field: 'sensor_tower_tag_camera_pov',
      label: 'dbi_detail_camera_pov',
      controlProps: {
        options: mockOptions.sensorTowerTagOptions?.sensor_tower_tag_camera_pov || [],
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.TAG_SELECT,
      field: 'sensor_tower_tag_product_model',
      label: 'dbi_detail_product_model',
      controlProps: {
        options: mockOptions.sensorTowerTagOptions?.sensor_tower_tag_product_model || [],
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.TAG_SELECT,
      field: 'sensor_tower_tag_setting',
      label: 'dbi_detail_setting',
      controlProps: {
        options: mockOptions.sensorTowerTagOptions?.sensor_tower_tag_setting || [],
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.TAG_SELECT,
      field: 'sensor_tower_tag_tags',
      label: 'dbi_detail_tags',
      controlProps: {
        options: mockOptions.sensorTowerTagOptions?.sensor_tower_tag_tags || [],
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.SENSOR_TOWER_THEME,
      field: 'sensor_tower_tag_theme',
      label: 'dbi_detail_theme',
      controlProps: {
        options: mockOptions.sensorTowerThemeOptions || [],
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.TAG_SELECT,
      field: 'sensor_tower_tag_monetization',
      label: 'dbi_detail_monetization',
      controlProps: {
        options: mockOptions.sensorTowerTagOptions?.sensor_tower_tag_monetization || [],
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.GAME_ENGINE,
      field: 'game_engine',
      controlProps: {
        options: mockOptions.engineOptions || [],
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.TAG_SELECT,
      field: 'appmagic_themes',
      label: APPMAGIC_TAG_TITLE_MAP.appmagic_themes,
      controlProps: {
        options: mockOptions.appmagicTagOptions?.appmagic_themes || [],
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.TAG_SELECT,
      field: 'appmagic_features',
      label: APPMAGIC_TAG_TITLE_MAP.appmagic_features,
      controlProps: {
        options: mockOptions.appmagicTagOptions?.appmagic_features || [],
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.TAG_SELECT,
      field: 'appmagic_ip',
      label: APPMAGIC_TAG_TITLE_MAP.appmagic_ip,
      controlProps: {
        options: mockOptions.appmagicTagOptions?.appmagic_ip || [],
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.TAG_SELECT,
      field: 'appmagic_settings',
      label: APPMAGIC_TAG_TITLE_MAP.appmagic_settings,
      controlProps: {
        options: mockOptions.appmagicTagOptions?.appmagic_settings || [],
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
    {
      type: FILTER_TYPE.TAG_SELECT,
      field: 'appmagic_art_styles',
      label: APPMAGIC_TAG_TITLE_MAP.appmagic_art_styles,
      controlProps: {
        options: mockOptions.appmagicTagOptions?.appmagic_art_styles || [],
      },
      inherit: [INHERIT_SOURCE_PAGE_ID],
    },
  ];
};

const getPanelValueContent = (value: IFilterablePageDemoPanelItem['value']): string => {
  if (typeof value === 'string') {
    return value;
  }

  return JSON.stringify(value, null, 2);
};

const FilterablePageDemo: React.FC = () => {
  const pageId = PAGE_ID;
  const filterSaveType = FILTER_SAVE_TYPE;
  const title = 'useFilterablePage Demo 列表页';
  const navigationText = '详情页';
  const confirmedTitle = 'Confirmed Value（已确认的值）';
  const logPrefix = 'FilterablePageDemo';
  const useSticky = true;
  const stickyTopOffset = 60;

  const dispatch = useDispatch();
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [mockOptions, setMockOptions] = useState<Record<string, any>>({});
  const [currentQuery, setCurrentQuery] = useState(formatSearchParamsForDisplay(history.location.search));
  const [filterValues, setFilterValues] = useState<IFilterablePageDemoValues>(DEFAULT_VALUES);
  const [draftValues, setDraftValues] = useState<IFilterablePageDemoValues>(DEFAULT_VALUES);
  const [syncResult, setSyncResult] = useState(EMPTY_SYNC_RESULT);
  const [isInitialFilterValuesLoading, setIsInitialFilterValuesLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    mockFetchOptions().then((data) => {
      if (!isMounted) {
        return;
      }

      setMockOptions(data);
      setOptionsLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * 同步 demo 面板展示值。
   * 这里同时更新 confirmed 和 draft，是为了让初始化结果在调试面板中保持一致。
   */
  const syncPanelValues = (values: IFilterablePageDemoValues): void => {
    setFilterValues((prevValues) => {
      return isEqual(prevValues, values) ? prevValues : values;
    });
    setDraftValues((prevValues) => {
      return isEqual(prevValues, values) ? prevValues : values;
    });
  };

  const { options: genreOptions, loading: genreOptionsLoading } = useGenreSourcesOptions({ entityType: 'mobile' });

  const items = useMemo(() => {
    return getFilterItems({
      genreOptions,
      mockOptions,
    });
  }, [genreOptions, mockOptions]);
  const hookReady = !optionsLoading && !genreOptionsLoading;

  const { getInitialFilterValues, initMeta, syncFilterState } = useFilterablePage({
    pageId,
    items,
    defaultValues: DEFAULT_VALUES,
    mergeStrategy: FilterMergeStrategy.MERGE_BY_PRIORITY,
    ready: hookReady,
    syncTargetConfig: {
      inherit: {
        enabled: true,
      },
    },
    conditions: {
      genreOptionsMap: genreOptions,
    },
  });

  const loading = optionsLoading || genreOptionsLoading || isInitialFilterValuesLoading;

  useEffect(() => {
    let isMounted = true;

    if (!hookReady) {
      setIsInitialFilterValuesLoading(true);
      return () => {
        isMounted = false;
      };
    }

    setIsInitialFilterValuesLoading(true);
    getInitialFilterValues()
      .then((nextFilterValues) => {
        if (!isMounted) {
          return;
        }

        syncPanelValues(nextFilterValues as IFilterablePageDemoValues);
        setCurrentQuery(formatSearchParamsForDisplay(history.location.search));
      })
      .finally(() => {
        if (isMounted) {
          setIsInitialFilterValuesLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [hookReady, items]);

  const apiParams = useMemo(() => {
    return buildFilterApiParams(items, filterValues);
  }, [filterValues, items]);

  /**
   * Confirm 后把 draft 提升为 confirmed，并交给 useFilterablePage 同步外部状态。
   */
  const handleConfirm = (values: IFilterablePageDemoValues): void => {
    syncPanelValues(values);
    syncFilterState(values)
      .then((result) => {
        setSyncResult(result);

        if (result.hasError) {
          console.debug(`[dbi] [${logPrefix}] 同步筛选状态存在失败项`, result);
        }
      })
      .finally(() => {
        setCurrentQuery(formatSearchParamsForDisplay(history.location.search));
      });
    console.debug(`[dbi] [${logPrefix}] 确认筛选`, values);
  };

  /** Reset 只重置 draft，不触发外部同步。 */
  const handleReset = (values: IFilterablePageDemoValues): void => {
    setDraftValues(values);
    console.debug(`[dbi] [${logPrefix}] 重置筛选（当前 Demo 配置为回默认值）`, values);
  };

  /** 处理 My Filter 加载后的 draft 变更。 */
  const handleFilterLoaded = (values: IFilterablePageDemoValues, meta: { changedFields: string[] }): void => {
    console.debug(`[dbi] [${logPrefix}] 加载方案`, values, meta.changedFields);
  };

  const panelItems = useMemo(() => {
    return buildFilterablePageDemoPanelItems({
      draftValues,
      filterValues,
      apiParams,
      currentQuery,
      initMeta,
      syncResult,
      confirmedTitle,
    });
  }, [apiParams, confirmedTitle, currentQuery, draftValues, filterValues, initMeta, syncResult]);

  /** 跳转前保存当前 draft 中声明了 inherit 的字段 */
  const handleNavigate = (): void => {
    navigateWithDraftInheritance({
      pageId,
      items,
      draftValues,
      dispatch,
      navigate: () => {
        history.push('/intelligence/filterable-page-demo/detail');
      },
    });
  };

  return (
    <Layout className={'dbi-filterable-page-demo'}>
      <Content>
        <Content.Header
          title={title}
          operation={
            <Button type="weak" onClick={handleNavigate}>
              {navigationText}
            </Button>
          }
        />
        <Content.Body>
          <Card className={'dbi-filterable-page-demo-filter-card'}>
            <Card.Body>
              <Filter
                loading={loading}
                items={items}
                value={filterValues}
                resetValue={DEFAULT_VALUES}
                useSticky={useSticky}
                stickyTopOffset={stickyTopOffset}
                onChange={(_field, _fieldValue, values) => {
                  setDraftValues(values as IFilterablePageDemoValues);
                }}
                onConfirm={handleConfirm}
                onReset={handleReset}
                onFilterLoaded={handleFilterLoaded}
                saveFilterType={filterSaveType}
                useActionBar
                useSaveFilter
                filterGroups={DEMO_FILTER_GROUPS}
              />
            </Card.Body>
          </Card>
          <div className={'dbi-filterable-page-demo-value-panel'}>
            {panelItems.map((item) => {
              return (
                <div key={item.key} className={'dbi-filterable-page-demo-value-panel-col'}>
                  <div className={'dbi-filterable-page-demo' + '-value-panel-title'}>{item.title}</div>
                  <pre className={'dbi-filterable-page-demo' + '-value-panel-content'}>
                    {getPanelValueContent(item.value)}
                  </pre>
                </div>
              );
            })}
          </div>
        </Content.Body>
      </Content>
    </Layout>
  );
};

export default FilterablePageDemo;
